/**
 * Transbank POS Integrado por Web Serial (Chrome / Edge).
 * Protocolo: <STX>DATOS<ETX><LRC>, LRC = XOR de DATOS + ETX. El POS responde ACK/NAK
 * y luego la respuesta; la caja confirma con ACK. 8N1, 115200 por defecto.
 */
const STX = 0x02, ETX = 0x03, ACK = 0x06, NAK = 0x15;
const enc = new TextEncoder(), dec = new TextDecoder("latin1");

export const TBK_RESPUESTAS = {
  "00": "Aprobado", "01": "Rechazado", "02": "Host no responde", "03": "Conexión falló",
  "04": "Transacción ya fue anulada", "05": "No existe transacción para anular", "06": "Tarjeta no soportada",
  "07": "Transacción cancelada desde el POS", "08": "No puede anular transacción débito", "09": "Error lectura tarjeta",
  "10": "Monto menor al mínimo", "11": "No existe venta", "12": "Transacción no soportada", "13": "Debe ejecutar cierre",
  "20": "Tarjeta inválida", "21": "Anulación no permitida", "22": "Tiempo agotado", "24": "Impresora sin papel",
  "26": "Debe cargar llaves", "27": "Debe actualizar", "67": "Máximo de ventas: ejecute cierre",
  "74": "Debe ejecutar cierre", "76": "Máximo de ventas: ejecute cierre", "77": "Debe ejecutar cierre",
  "78": "Esperando lectura de tarjeta", "79": "Confirmando monto", "80": "Seleccionando cuotas",
  "81": "Ingresando clave", "82": "Enviando transacción a Transbank", "93": "Declinada",
};
export const TBK_MARCAS = { VI: "VISA", MC: "MASTERCARD", AX: "AMEX", DC: "DINERS", DB: "REDCOMPRA", MG: "MAGNA", TM: "MAS", RP: "RIPLEY", EX: "EXTRA", TC: "CMR", CA: "CABAL", CR: "CREDENCIAL", TP: "PRESTO", CE: "CERRADA" };

export const tbkSoportado = () => typeof navigator !== "undefined" && "serial" in navigator;

export function frame(datos) {
  const body = enc.encode(datos);
  let lrc = ETX;
  for (const b of body) lrc ^= b;
  return new Uint8Array([STX, ...body, ETX, lrc]);
}

let port = null, reader = null, buffer = [];

const PORT_KEY = "aipos_tbk_port";

function elegirConocido(conocidos) {
  let guardado = null;
  try { guardado = JSON.parse(localStorage.getItem(PORT_KEY) || "null"); } catch (_) { /* sin dato */ }
  if (guardado?.usbVendorId) {
    const p = conocidos.find((x) => { const i = x.getInfo(); return i.usbVendorId === guardado.usbVendorId && i.usbProductId === guardado.usbProductId; });
    if (p) return p;
  }
  return conocidos[0] || null;
}

async function abrir(pedir) {
  if (port?.readable) return port;
  port = elegirConocido(await navigator.serial.getPorts());
  if (!port && pedir) {
    try { port = await navigator.serial.requestPort(); }
    catch (_) { throw new Error("No se eligió el puerto del POS Transbank"); }
  }
  if (!port) throw new Error("Conecta el POS Transbank: elige el puerto en Terminal → Transbank");
  try { localStorage.setItem(PORT_KEY, JSON.stringify(port.getInfo())); } catch (_) { /* sin storage */ }
  try {
    await port.open({ baudRate: 115200, dataBits: 8, stopBits: 1, parity: "none" });
  } catch (e) {
    port = null;
    throw new Error(/open|InvalidState/i.test(`${e?.name} ${e?.message}`)
      ? "El puerto del POS Transbank está ocupado: cierra otras pestañas de Ai-POS o la configuración del terminal"
      : (e?.message || "No se pudo abrir el puerto del POS Transbank"));
  }
  reader = port.readable.getReader();
  buffer = [];
  return port;
}

export async function tbkConectar() { await abrir(true); return true; }
export async function tbkDesconectar() {
  try { await reader?.cancel(); reader?.releaseLock(); await port?.close(); } catch (_) { /* ya cerrado */ }
  port = null; reader = null;
}

async function leerByte(ms) {
  const fin = Date.now() + ms;
  while (!buffer.length) {
    const restante = fin - Date.now();
    if (restante <= 0) return null;
    const r = await Promise.race([reader.read(), new Promise((res) => setTimeout(() => res(null), restante))]);
    if (r?.value) buffer.push(...r.value);
    if (r?.done) throw new Error("Se desconectó el POS Transbank");
  }
  return buffer.shift();
}

async function escribir(bytes) {
  const w = port.writable.getWriter();
  try { await w.write(bytes); } finally { w.releaseLock(); }
}

/** Lee un mensaje completo <STX>...<ETX><LRC>; responde ACK (o NAK si el LRC no cuadra). */
async function leerMensaje(ms) {
  for (;;) {
    let b;
    do { b = await leerByte(ms); if (b === null) throw new Error("El POS Transbank no respondió a tiempo"); } while (b !== STX);
    const datos = [];
    while ((b = await leerByte(5000)) !== ETX) { if (b === null) throw new Error("Respuesta incompleta del POS"); datos.push(b); }
    const lrc = await leerByte(5000);
    let calc = ETX;
    for (const x of datos) calc ^= x;
    if (calc !== lrc) { await escribir(new Uint8Array([NAK])); continue; }
    await escribir(new Uint8Array([ACK]));
    return dec.decode(new Uint8Array(datos));
  }
}

/** Envía un comando (reintenta 2 veces con NAK) y devuelve la respuesta final del POS. */
async function comando(datos, { esperaRespuesta = true, timeout = 150000, onEstado, pedir = false } = {}) {
  await abrir(pedir);
  for (let i = 0; i < 3; i++) {
    buffer = [];
    await escribir(frame(datos));
    const r = await leerByte(3000);
    if (r === ACK) break;
    if (i === 2) throw new Error("El POS Transbank no aceptó el comando");
  }
  if (!esperaRespuesta) return null;
  for (;;) {
    const msg = await leerMensaje(timeout);
    const campos = msg.split("|");
    // Mensajes intermedios (0900|78 …): se muestran y se sigue esperando
    if (campos[0] === "0900") { onEstado?.(TBK_RESPUESTAS[campos[1]] || `Estado ${campos[1]}`); continue; }
    return campos;
  }
}

const respuesta = (c) => ({ codigo: c[1], aprobado: c[1] === "00", mensaje: TBK_RESPUESTAS[c[1]] || `Código ${c[1]}` });

const venta = (c) => ({
  ...respuesta(c), comercio: c[2], terminal: c[3], ticket: c[4], autorizacion: c[5],
  monto: Number(c[6] || 0), cuotas: Number(c[7] || 0), monto_cuota: Number(c[8] || 0), ultimos4: c[9],
  operacion: c[10], tipo_tarjeta: c[11] === "DB" ? "DEBITO" : c[11] === "CR" ? "CREDITO" : c[11],
  fecha_contable: c[12], num_cuenta: c[13], marca: TBK_MARCAS[c[14]] || c[14], fecha: c[15], hora: c[16],
});

export async function tbkVenta(monto, ticket, onEstado) {
  const t = String(ticket || "").replace(/\W/g, "").slice(-6) || "0";
  return venta(await comando(`0200|${Math.round(monto)}|${t}||0|1|`, { onEstado, pedir: true }));
}
export const tbkUltimaVenta = async () => venta(await comando("0250|"));
export async function tbkAnulacion(operacion) {
  const c = await comando(`1200|${String(operacion).trim()}|`);
  return { ...respuesta(c), comercio: c[2], terminal: c[3], autorizacion: c[4], operacion: c[5] };
}
export const tbkCierre = async () => respuesta(await comando("0500||", { timeout: 60000 }));
export async function tbkTotales() {
  const c = await comando("0700||", { pedir: true, timeout: 30000 });
  return { ...respuesta(c), cantidad: Number(c[2] || 0), total: Number(c[3] || 0) };
}
export const tbkCargaLlaves = async () => respuesta(await comando("0800"));
export async function tbkPoll() { await comando("0100", { esperaRespuesta: false }); return true; }
